import Vue, { DirectiveOptions, VNode } from 'vue';
import Vuelidate from 'vuelidate';
import axios from 'axios';
import VueAxios from 'vue-axios';
import VueGtm from 'vue-gtm';
import App from './App.vue';
import router from './router';
import store from './store';
import vuetify from './plugins/vuetify';
import i18n from './i18n';
import './styles/app.scss';

Vue.use(Vuelidate);

/**
 * Resolves a translation key such as "form.errors.required" to its
 * corresponding vuelidate validation result on the target object.
 */
function resolvePath(path: string, obj?: unknown): unknown {
  return path.split('.').reduce((acc: any, key: string) => (acc ? acc[key] : null), obj || self);
}

Vue.config.productionTip = false;

Vue.mixin({
  methods: {
    validate(this: any, isValid: boolean, event: FocusEvent): void {
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      if (isValid !== true && event !== undefined
        && (relatedTarget === undefined || relatedTarget === null || relatedTarget.id !== 'cancel')) {
        const target = event.target as HTMLInputElement;
        if (target.id === '') {
          console.warn('Cannot validate field without id', target);
        }
        const validationResult: any = resolvePath(target.id, isValid);
        Vue.set(this.$data.errors, target.id, undefined);
        // eslint-disable-next-line no-restricted-syntax
        for (const key in validationResult) {
          if (Object.prototype.hasOwnProperty.call(validationResult, key)) {
            const passed = validationResult[key];
            if (!key.startsWith('$') && passed === false) {
              const params = validationResult.$params[key];
              const code = `${this.$options.name}.${target.id}.errors.${key}`;
              Vue.set(this.$data.errors, target.id, { code, message: i18n.t(code, params) });
            }
          }
        }
      }
    },
  },
});

Vue.filter('errormessage', (error: any) => {
  if (error === undefined || error === '') return '';
  return i18n.t(error.code, error.context);
});

Vue.filter('errorcode', (error: any) => {
  if (error === undefined || error === '') return '';
  return error.code;
});

Vue.filter('convertISOToDate', (value: string) => {
  if (value === undefined || value === '') return '';
  const isoDate = value.substr(0, 10);
  const [year, month, day] = isoDate.split('-');
  return i18n.d(new Date(+year, +month - 1, +day), 'short');
});

Vue.directive('validate', (function validateDirective(): DirectiveOptions {
  const getVModel = (vnode: VNode) => (vnode.data && vnode.data.directives
    ? vnode.data.directives.filter((d) => d.name === 'model')[0] : undefined);

  const getValidation = (vnode: VNode) => {
    const model = getVModel(vnode);
    return model && (vnode.context as any).$v[model.expression as string];
  };

  const setAriaInvalid = (el: HTMLElement, validation: any) => {
    if (validation) {
      el.setAttribute('aria-invalid', String(validation.$dirty && validation.$invalid));
    }
  };

  const touchAll = (vnode: VNode) => {
    const context = vnode.context as any;
    context.$v.$touch();
    context.fields.forEach((el: HTMLElement) => {
      const entry = context.modelMap.get(el);
      const expression = entry && entry.expression;
      if (expression) setAriaInvalid(el, context.$v[expression]);
    });
    if (context.$v.$dirty && context.$v.$invalid) {
      context.form.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return {
    bind(el, binding, vnode) {
      const validation = getValidation(vnode);
      const context = vnode.context as any;
      if (!context.modelMap) {
        context.fields = [];
        context.modelMap = new WeakMap();
      }
      el.addEventListener('change', () => {
        if (validation) validation.$touch();
      }, false);
    },
    inserted(el, binding, vnode) {
      const context = vnode.context as any;
      if (!context.form) {
        context.form = el.closest('.form');
        context.form.addEventListener('submit', (event: Event) => {
          event.preventDefault();
          touchAll(vnode);
        });
      }
      context.fields.push(el);
      context.modelMap.set(el, getVModel(vnode));
    },
    update(el, binding, vnode) {
      setAriaInvalid(el, getValidation(vnode));
    },
  };
}()));

Vue.use(VueGtm, { refreshOnceOnNavigation: true });
Vue.use(VueAxios, axios);

axios.interceptors.request.use((config) => {
  // eslint-disable-next-line no-param-reassign
  config.headers.Accept = 'application/json';
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response.status === 401) {
      console.error('Forcing logout!');
      store.dispatch('authStore/logout');
      router.push('/login');
    }
    return Promise.reject(error);
  },
);

Vue.prototype.$currentHref = window.location.href;

const app = new Vue({
  router,
  vuetify,
  store,
  i18n,
  render: (h) => h(App),
}).$mount('#app');

(window as any).app = app;
